import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditIcon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useTeamsQueryOptions } from "@/components/tournaments/context";
import type { Division, TournamentDivision } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";

export type SetCapacityFormProps = {
  division: TournamentDivision & { division: Division };
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function SetCapacityForm({
  division,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger,
}: SetCapacityFormProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledIsOpen ?? uncontrolledOpen;
  const setOpen = controlledOnOpenChange ?? setUncontrolledOpen;

  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const teamsQueryOptions = useTeamsQueryOptions();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.setCapacity.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());
      queryClient.invalidateQueries(teamsQueryOptions);
    },
  });

  const schema = z.object({
    capacity: z.number().min(0),
    waitlistCapacity: z.number().min(0),
  });

  const form = useAppForm({
    defaultValues: {
      capacity: division.capacity,
      waitlistCapacity: division.waitlistCapacity,
    },
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { capacity, waitlistCapacity } }) => {
      mutate({
        id: division.id,
        capacity,
        waitlistCapacity,
      });

      setOpen(false);
    },
  });

  return (
    <>
      {!hideTrigger && (
        <Button variant="text" onPress={() => setOpen(true)}>
          <span className="whitespace-nowrap">
            <span className="md:hidden">Cap: </span>
            <span className="hidden md:inline">Capacity: </span>
            {division.capacity}
          </span>
          <span className="whitespace-nowrap">
            <span className="md:hidden">WL Cap: </span>
            <span className="hidden md:inline">Waitlist Capacity: </span>
            {division.waitlistCapacity}
          </span>
          <EditIcon size={16} />
        </Button>
      )}
      <Modal isOpen={isOpen} onOpenChange={setOpen}>
        <div className="relative flex flex-col space-y-4 p-3">
          <Heading className={title({ size: "sm" })} slot="title">
            Set Capacity
          </Heading>

          <form
            className="flex flex-col space-y-6"
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            {failureReason && (
              <form.AppForm>
                <form.Alert
                  title={"Unable to set capacity"}
                  description={failureReason.message}
                />
              </form.AppForm>
            )}

            <form.AppField
              name="capacity"
              children={(field) => (
                <field.Number label="Capacity" field={field} minValue={1} />
              )}
            />

            <form.AppField
              name="waitlistCapacity"
              children={(field) => (
                <field.Number
                  label="Waitlist Capacity"
                  field={field}
                  minValue={0}
                />
              )}
            />

            <form.AppForm>
              <form.Footer>
                <Button onPress={() => setOpen(false)}>Cancel</Button>
                <form.SubmitButton>Save</form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </>
  );
}
